import { POIv3 } from "common/Map/POI";
import { PhotoClusters } from "common/Maplet/PhotoClusters";
import { MapV2 } from "common/MapV2";
import { featureTypes } from "common/MapV2/featureTypes";
import { TrackLine } from "common/MapV2/TrackLine";
import { TrackSnapPoint } from "common/MapV2/TrackSnapPoint";
import { SampleGraph } from "common/SampleGraph";
import { withMetricUnits } from "common/units/MetricUnitsContext";
import { TrackLocation } from "data/records";
import { photoSelectors } from "data/resources/photos";
import { pointOfInterestSelectors } from "data/resources/pointsOfInterest";
import { renderHoveredFeature, renderSelectedFeature } from "features/troute_show/TRouteShowPage/mapRendering";
import { Set } from "immutable";
import { initialViewportForTRoute } from "lib/geoUtils";
import { tileServices } from "lib/tileServices";
import { getPreferences } from "lib/utils";
import _ from "lodash";
import p from "prop-types";
import React from "react";
import { connect } from "react-redux";
import styles from "./MapAndGraph.scss";

// use this component if you just want to put your troute on a map, dang it
// MapAndGraph takes care of
// - drawing your troute's trackline
// - keeping hovered/selected features in sync bw map and graph
//
// via mapSettings, you can control initial:
// - overlay
//
// as well as whether to display
// - troute photos
// - troute POI
// - distanceMarkers
//
// STYLE NOTE: by default this component just takes up 100% height and width.
// you can either put your styles on a containing element, or pass a height
// value in with props
export class _MapAndGraph extends React.PureComponent {
  propTypes = {
    troute: p.object.isRequired,
    objectType: p.string.isRequired,
    metricUnits: p.bool.isRequired,
    height: p.string,
  };

  static defaultProps = {
    mapSettings: {
      overlay: tileServices.rwgps,
      showPhotos: false,
      showPOIs: false,
      distanceMarkers: false,
    },
  };

  constructor(props) {
    super(props);

    const initialViewport = initialViewportForTRoute(props.troute);
    const scrollWheelZoom = getPreferences().route_viewer_enable_scrollwheel_zoom ?? true;
    const { overlay, showPhotos, showPOIs, distanceMarkers } = props.mapSettings;

    this.state = {
      overlay,
      initialViewport,
      selectedFeature: null,
      hoveredFeature: null,
      zoom: initialViewport.zoom,
      metricsEnabled: Set(["ele"]),
      mapSettings: {
        // put these in state in case we want to let the user fiddle with them
        scrollWheelZoom,
        showPhotos,
        showPOIs,
        distanceMarkers,
      },
    };
  }

  onViewportChanged = (newViewport) => {
    this.setState({ zoom: newViewport.zoom });
  };

  onFeatureHovered = (hoveredFeature) => {
    this.setState({ hoveredFeature });
  };

  onFeatureSelected = (selectedFeature) => {
    this.setState({ selectedFeature, hoveredFeature: null });
  };

  renderSelectedFeature = renderSelectedFeature.bind(this);

  renderHoveredFeature = renderHoveredFeature.bind(this);

  clearSelectedFeature = () => {
    this.setState({ selectedFeature: null });
  };

  clearHoveredFeature = () => {
    this.setState({ hoveredFeature: null });
  };

  onMapSnap = ({ snapPoint, snapIndex }) => {
    this.onFeatureHovered({
      type: featureTypes.POINT_GENERIC,
      data: { highlightPoint: { trackPoint: snapPoint, index: snapIndex } },
    });
  };

  getSGHighlightPoint = () => {
    const { hoveredFeature } = this.state;
    if (!_.get(hoveredFeature, "data.highlightPoint")) {
      return undefined;
    }
    // make a dummy {index} obj to match the older highlightPoint format
    // from RSPv2
    return {
      index: hoveredFeature.data.highlightPoint.index,
    };
  };

  getSGHighlightSlice = () => {
    const { selectedFeature } = this.state;
    if (!this.hasHighlightSlice()) {
      return undefined;
    }
    // for now, the sample graph can't handle TrackLocation synthetic
    // points, so we convert to integers
    return selectedFeature.data.highlightSlice.map(TrackLocation.closestIndex);
  };

  getSGHoverSlice = () => {
    const { hoveredFeature } = this.state;
    if (!this.hasHoverSlice()) {
      return undefined;
    }
    // for now, the sample graph can't handle TrackLocation synthetic
    // points, so we convert to integers
    return hoveredFeature.data.highlightSlice.map(TrackLocation.closestIndex);
  };

  hasHighlightSlice = () => {
    const { selectedFeature } = this.state;
    return selectedFeature && selectedFeature.data && selectedFeature.data.highlightSlice;
  };

  hasHoverSlice = () => {
    const { hoveredFeature } = this.state;
    return hoveredFeature && hoveredFeature.data && hoveredFeature.data.highlightSlice;
  };

  onSliceHighlighted = (segment) => {
    this.onFeatureSelected({
      type: featureTypes.SAMPLE_GRAPH_USER_SELECTION,
      firstI: segment[0].left,
      data: { highlightSlice: segment },
    });
  };

  onSGPointHighlighted = (p) => {
    const { troute } = this.props;
    if (p === null) {
      // SG calls this with null sometimes to clear the point
      this.onFeatureHovered(null);
      return;
    }

    const trackPoint = troute.trackPoints[p.index];
    this.onFeatureHovered({
      type: featureTypes.POINT_GENERIC,
      data: { highlightPoint: { trackPoint, index: p.index } },
    });
  };

  render() {
    const { troute, troutePhotos, troutePOIs, objectType, metricUnits, height } = this.props;
    const { overlay, initialViewport, hoveredFeature, zoom, mapSettings, metricsEnabled } = this.state;
    const mapHeight = height - 145 + "px"; // lol

    return (
      <div style={{ height: height + "px" }} className={styles.MapAndGraph}>
        <div style={{ height: mapHeight }}>
          <MapV2
            scrollWheelZoom={mapSettings.scrollWheelZoom}
            overlay={overlay}
            onChangeOverlay={(overlay) => this.setState({ overlay })}
            ref={(r) => {
              this._map = r;
            }}
            onViewportChanged={this.onViewportChanged}
            initialViewport={initialViewport}
          >
            <TrackLine trackPoints={troute.trackPoints} showDistanceMarkers={mapSettings.showDistanceMarkers} />
            <TrackSnapPoint
              hoveredFeature={hoveredFeature}
              troute={troute}
              onSnap={this.onMapSnap}
              onUnSnap={this.clearHoveredFeature}
            />
            {/* TODO refactor troutePhotos to accept photos as a separate prop */}
            {mapSettings.showPhotos && troutePhotos && (
              <PhotoClusters troute={troute.set("photos", troutePhotos)} clusterStrategy={objectType} zoom={zoom} />
            )}
            {mapSettings.showPOIs &&
              troutePOIs &&
              troutePOIs.map((poi) => <POIv3 key={poi.id} poi={poi} photos={troutePhotos} />)}
            {this.renderSelectedFeature(false)}
            {this.renderHoveredFeature()}
          </MapV2>
        </div>
        <div className={styles.graph}>
          <SampleGraph
            ref={(r) => {
              this._sgComponent = r;
            }}
            plotOnly
            dragMode="zoom"
            segmentFormat="TrackLocation"
            rawPoints={troute.rawPoints}
            highlightPoint={this.getSGHighlightPoint()}
            highlightSlice={this.getSGHighlightSlice()}
            hoverSlice={this.getSGHoverSlice()}
            onSegmentSelected={this.onSliceHighlighted}
            onSegmentUnselected={() => this.onFeatureSelected(null)}
            onPointHighlighted={this.onSGPointHighlighted}
            metricsEnabled={metricsEnabled}
            metricUnits={metricUnits}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  troutePhotos: ownProps.troute && photoSelectors.findByIds(state, ownProps.troute.photoIds),
  troutePOIs: ownProps.troute && pointOfInterestSelectors.findByIds(state, ownProps.troute.poiIds),
});

export const MapAndGraph = connect(mapStateToProps, null)(withMetricUnits(_MapAndGraph));
